import React, { createContext, useReducer, useEffect, useMemo } from 'react'
import objectPath from 'object-path'
import httpClient from '../utility/httpClient.util'
import cloneDeep from 'clone-deep'
import { preserveOpenModalState } from './preserveOpenModalState'
import { convertElementSchema } from '../designer/component/system/menu/formElements/data/element'
import applyUiDefaults from '../designer/component/action/ensureUiDefaults'
import { createDesign as createDesignAction } from '../designer/component/action/Design'
import { createTabActiveState } from '../designer/component/container/tab/TabHelper'

const initialState = {
  jsonForm: {
    module: '',
    formName: '',
    formCode: '',
    schema: {},
    uischema: {},
    formData: {},
    formGroup: [],
    apiGroup: [],
    customGroup: [],
    tableGroup: [],
    favorites: [],
    conditions: [],
    collection: {
      map: {},
      jsonKey: {},
      jsonName: {},
    },
    formRevisionId: null,
    layoutHistory: {
      version: {
        current: 0,
        restore: 0,
      },
      subVersion: {
        current: 0,
        restore: 0,
      },
    },
    require: { input: {}, checked: {} },
    validation: { input: {}, inputMask: {}, date: {} },
    enable: true,
    props: {},
    mapFormId: {},
    mapPageId: {},
    designerPropsData: { map: {}, data: {}, receive: [] },
    componentPropsData: {
      map: {},
      data: {},
      receive: [],
      pagination: { page: 1, rowsPerPage: 10, totalCount: 0 },
    },
    isAutoSave: true,
    autoSaveTime: 60,
    dataset: [],
    formulas: [],
    rules: [],
  },
  searchData: {},
  searchResult: [],
  modalForm: null,
  formDesign: {
    formModuleId: null,
    formDesignId: null,
    formRevisionId: null,
  },
  editorKey: '',
  editorDropId: '',
  current: { ui: { uiType: 'layout' }, schema: null, readOnly: false },
  openModal: null,
  openModalCondition: {},
  modules: [],
  newUi: null,
  tempUi: [],
  tempModal: [],
  tempComponent: [],
  perspectiveRoute: '',
  dataGET: {},
  sendProps: {},
  showBindData: false,
  showBindParam: false,
  categories: [],
  widgetComponent: {},
  modalComponent: {},
  isSyncWidget: false,
  dashboardType: [],
  openSetting: false,
  fullscreen: false,
  dashboardList: [],
  filterDashboard: {},
  isLoadingDashboard: true,
  isUiChanged: {},
  isSelectRow: false,
  tableRefresh: [],
  isResetData: false,
  tabParamActive: {},
  datasources: {},
  isAddDynamic: false,
  isUpdateDynamic: false,
  isLoading: false,
  keywordFilter: '',
  allData: {},
  showInvisible: false,
  elementAction: {},
  pagination: { page: 1, rowsPerPage: 10, totalCount: 0 },
  displayMode: 'designer',
  '@datasource': {},
  isInitalOption: false,
  isCheckedCondition: false,
  currentSearch: {
    uiKey: '',
    search: {},
  },
  activeButton: '',
  isInputSearch: false,
  widgetJsonForm: {},
  callback: {},
  apiCondition: [],
  designerEvent: {},
  isLoadingApi: false,
  selectApiOption: {},
  allFormGroup: [],
  employeeList: [],
  widgetDesign: {},
  documentJson: {},
  searchTable: [],
  isClearData: false,
}
const cloneInitialState = cloneDeep(initialState)
const DesignContext = createContext(initialState)
const { Provider } = DesignContext

const setObjectPathValue = (data, key, payload) => {
  let path = String(key || '')
  let parentPath = path.split('.').slice(0, -1).join('.')
  let parentValue = parentPath ? objectPath.get(data, parentPath) : data
  if (
    parentPath &&
    (parentValue === undefined ||
      parentValue === null ||
      typeof parentValue !== 'object')
  ) {
    objectPath.set(data, parentPath, {})
  }
  objectPath.set(data, path, payload)
}

const DESIGNER_ON_CHANGE_PROP_TYPE = 'onChange'
const DESIGNER_ON_CHANGE_SCOPE_ATTRIBUTE = 'data-designer-on-change-scope'
const DESIGNER_ON_CHANGE_TIMEOUT_MS = 1000
const DESIGNER_ON_CHANGE_EVENT_STATE = { scopeKey: '', updatedAt: 0 }
const DESIGNER_ON_CHANGE_ACTIVE_SCOPES = {}
const DESIGNER_ON_CHANGE_CHANGED_DATA = {}
const DESIGNER_ON_CHANGE_CHANGED_META = {}
const DESIGNER_ON_CHANGE_PENDING = {}

const isDesignerOnChangeUserEvent = (event = {}) => {
  if (event.isTrusted === false) return false

  const target = event.target

  if (!target?.closest) return false

  return Boolean(
    target.closest(
      [
        '.custom-option',
        '.custom-single-value',
        '[contenteditable="true"]',
        '[role="combobox"]',
        '[role="option"]',
        '[class*="ValueContainer"]',
        '[class*="indicator"]',
        '[class*="menu"]',
        '[class*="multiValue"]',
        '[class*="placeholder"]',
        '[class*="select__control"]',
        '[class*="-control"]',
        '[class*="singleValue"]',
        'button',
        'input',
        'select',
        'textarea',
      ].join(','),
    ),
  )
}

const getDesignerOnChangeEventScopeKey = (event = {}) => {
  const target = event.target

  if (!target?.closest) return ''

  return (
    target
      .closest(`[${DESIGNER_ON_CHANGE_SCOPE_ATTRIBUTE}]`)
      ?.getAttribute(DESIGNER_ON_CHANGE_SCOPE_ATTRIBUTE) || ''
  )
}

const markDesignerOnChangeUserEvent = (event) => {
  if (!isDesignerOnChangeUserEvent(event)) return

  DESIGNER_ON_CHANGE_EVENT_STATE.scopeKey =
    getDesignerOnChangeEventScopeKey(event)
  DESIGNER_ON_CHANGE_EVENT_STATE.updatedAt = Date.now()
}

const hasRecentDesignerOnChangeUserEvent = (state = {}) => {
  const isRecent =
    Date.now() - DESIGNER_ON_CHANGE_EVENT_STATE.updatedAt <
    DESIGNER_ON_CHANGE_TIMEOUT_MS
  const scopeKey = getDesignerOnChangeScopeKey(state)

  return (
    isRecent &&
    !!DESIGNER_ON_CHANGE_EVENT_STATE.scopeKey &&
    DESIGNER_ON_CHANGE_EVENT_STATE.scopeKey === scopeKey
  )
}

const stringifyDesignerOnChangeValue = (value) => {
  try {
    return JSON.stringify(value)
  } catch (error) {
    return String(value)
  }
}

const isSameDesignerOnChangeValue = (oldValue, newValue) => {
  if (oldValue === newValue) return true

  return (
    stringifyDesignerOnChangeValue(oldValue) ===
    stringifyDesignerOnChangeValue(newValue)
  )
}

const getDesignerOnChangeScopeKey = (state = {}, propName = '') => {
  const designCode =
    objectPath.get(state, 'designCode') ||
    objectPath.get(state, 'formDesign.code') ||
    objectPath.get(state, 'jsonForm.formCode') ||
    objectPath.get(state, 'formDesign.formDesignId') ||
    ''
  const urlParams = objectPath.get(state, 'urlParams') || {}

  return [
    designCode,
    urlParams.vn,
    urlParams.hn,
    urlParams.an,
    propName,
  ].filter(Boolean).join(':')
}

const registerDesignerOnChangeScope = ({
  props = [],
  state = {},
}) => {
  const onChangeProps = props.filter(
    (prop = {}) => prop.propType === DESIGNER_ON_CHANGE_PROP_TYPE,
  )
  const propNames = onChangeProps.map((prop = {}) => prop.prop).filter(Boolean)
  const scopeKey = getDesignerOnChangeScopeKey(state)

  if (!scopeKey) return

  if (!propNames.length) {
    delete DESIGNER_ON_CHANGE_ACTIVE_SCOPES[scopeKey]
    return
  }

  DESIGNER_ON_CHANGE_ACTIVE_SCOPES[scopeKey] = propNames
}

const getDesignerOnChangeProps = ({
  receiveProps = [],
  state = {},
}) => {
  const scopeKey = getDesignerOnChangeScopeKey(state)
  const propNames = DESIGNER_ON_CHANGE_ACTIVE_SCOPES[scopeKey] || []

  if (!propNames.length) return []

  return receiveProps.filter(
    (prop = {}) =>
      prop.propType === DESIGNER_ON_CHANGE_PROP_TYPE &&
      propNames.includes(prop.prop),
  )
}

const getDesignerOnChangeFieldName = (state = {}, key) => {
  const field = objectPath.get(state, ['jsonForm', 'schema', 'mapName', key]) ||
    key
  const fieldParts = String(field || '').split('.')

  return fieldParts[fieldParts.length - 1] || field
}

const flushDesignerOnChange = (pendingKey) => {
  const pending = DESIGNER_ON_CHANGE_PENDING[pendingKey]

  if (!pending) return

  delete DESIGNER_ON_CHANGE_PENDING[pendingKey]

  try {
    const response = pending.onChange(pending.changedData, pending.meta)

    if (response?.catch) response.catch(console.error)
  } catch (error) {
    console.error(error)
  }
}

const queueDesignerOnChange = ({
  changedData,
  field,
  formData,
  key,
  onChange,
  previousValue,
  prop,
  state,
  value,
}) => {
  const propName = prop.prop || ''
  const pendingKey = getDesignerOnChangeScopeKey(state, propName)
  const changedMeta = {
    field,
    key,
    previousValue,
    value,
  }
  const allChangedData = {
    ...(DESIGNER_ON_CHANGE_CHANGED_DATA[pendingKey] || {}),
    ...changedData,
  }
  const allChangedMeta = [
    ...(DESIGNER_ON_CHANGE_CHANGED_META[pendingKey] || []).filter(
      (change) => change.field !== field,
    ),
    changedMeta,
  ]
  const pending = DESIGNER_ON_CHANGE_PENDING[pendingKey] || {
    changedData: {},
    meta: {
      changes: [],
      prop: propName,
      propConfig: prop,
    },
    onChange,
  }

  DESIGNER_ON_CHANGE_CHANGED_DATA[pendingKey] = allChangedData
  DESIGNER_ON_CHANGE_CHANGED_META[pendingKey] = allChangedMeta

  pending.changedData = allChangedData
  pending.meta = {
    ...pending.meta,
    field,
    formData,
    key,
    name: field,
    previousValue,
    value,
    changes: allChangedMeta,
  }
  pending.onChange = onChange

  DESIGNER_ON_CHANGE_PENDING[pendingKey] = pending

  if (!pending.timer) {
    pending.timer = setTimeout(() => flushDesignerOnChange(pendingKey), 0)
  }
}

const notifyDesignerOnChange = ({
  isDelete = false,
  key,
  payload,
  state,
}) => {
  if (!key) return
  if (String(key).startsWith('@')) return
  if (!hasRecentDesignerOnChangeUserEvent(state)) return

  const receiveProps =
    objectPath.get(state, 'jsonForm.designerPropsData.receive') || []
  const callback = objectPath.get(state, 'callback') || {}
  const formData = objectPath.get(state, 'jsonForm.formData') || {}
  const searchData = objectPath.get(state, 'searchData') || {}
  const onChangeProps = getDesignerOnChangeProps({ receiveProps, state })

  if (!onChangeProps.length) return

  const nextFormData = cloneDeep(formData)

  if (isDelete) {
    objectPath.del(nextFormData, key)
  } else {
    setObjectPathValue(nextFormData, key, payload)
  }

  onChangeProps.forEach((prop = {}) => {
    const propName = prop.prop || ''
    const onChange = objectPath.get(callback, propName)

    if (typeof onChange !== 'function') return

    const previousValue = objectPath.has(formData, key)
      ? objectPath.get(formData, key)
      : objectPath.get(searchData, key)
    const value = isDelete ? undefined : payload

    if (isSameDesignerOnChangeValue(previousValue, value)) return

    try {
      const field = getDesignerOnChangeFieldName(state, key)
      const changedData = {
        [field]: value,
      }
      queueDesignerOnChange({
        changedData,
        field,
        formData: nextFormData,
        key,
        onChange,
        previousValue,
        prop,
        state,
        value,
      })
    } catch (error) {
      console.error(error)
    }
  })
}

const DesignReducer = (state, { type, key, payload, isDelete }) => {
  switch (type) {
    case 'RESET_CONTEXT':
      let modules = state.modules
      let employeeList = state.employeeList
      let widgetDesign = state.widgetDesign
      if (payload === true) {
        state = cloneInitialState
        return state
      }
      state = {
        ...initialState,
        ...payload,
        modules,
        employeeList,
        widgetDesign,
      }
      return state
    case 'CLEAR_DATA':
      state = {
        ...state,
        isClearData: true,
        jsonForm: {
          ...state.jsonForm,
          formData: {},
        },
        isUiChanged: {
          ...state.isUiChanged,
          autocompleteOption: {},
        },
        allData: {},
        ['@datasource']: {},
      }
      return state
    case 'IS_CLEAR_DATA':
      return { ...state, isClearData: false, isResetData: true }
    case 'SET_SCHEMA':
      return { ...state, jsonForm: { ...state.jsonForm, schema: payload } }
    case 'SET_UI_SCHEMA':
      return {
        ...state,
        jsonForm: {
          ...state.jsonForm,
          uischema:
            typeof payload === 'function'
              ? payload(state.jsonForm.uischema)
              : payload,
        },
      }
    case 'SET_FORM_DATA':
      return {
        ...state,
        jsonForm: {
          ...state.jsonForm,
          formData: payload,
        },
      }
    case 'SET_FIELD_FORM_DATA':
      if (isDelete) {
        objectPath.del(state, `jsonForm.formData.${key}`)
      } else {
        setObjectPathValue(state, `jsonForm.formData.${key}`, payload)
      }
      return state
    case 'SET_DESIGNER_PROPS_DATA':
      return {
        ...state,
        jsonForm: { ...state.jsonForm, designerPropsData: payload },
      }
    case 'SET_COMPONENT_PROPS_DATA':
      return {
        ...state,
        jsonForm: { ...state.jsonForm, componentPropsData: payload },
      }
    case 'SET_FORM_GROUP':
      objectPath.set(state, 'jsonForm.formGroup', payload)
      return state
    case 'SET_BULK_GROUP':
      objectPath.set(state, 'jsonForm.bulkGroup', payload)
      return state
    case 'SET_DATASET':
      objectPath.set(state, 'jsonForm.dataset', payload)
      return state
    case 'SET_API_GROUP':
      objectPath.set(state, 'jsonForm.apiGroup', payload)
      return state
    case 'SET_API_CONDITION_GROUP':
      objectPath.set(state, 'jsonForm.apiCondition', payload)
      return state
    case 'SET_API_SEARCH_TABLE':
      objectPath.set(state, 'jsonForm.searchTable', payload)
      return state

    case 'SET_CUSTOM_GROUP':
      return { ...state, jsonForm: { ...state.jsonForm, customGroup: payload } }
    case 'SET_TABLE_GROUP':
      return { ...state, jsonForm: { ...state.jsonForm, tableGroup: payload } }
    case 'SET_REQUIRE_FIELD':
      return { ...state, jsonForm: { ...state.jsonForm, require: payload } }
    case 'SET_VALIDATION':
      return { ...state, jsonForm: { ...state.jsonForm, validation: payload } }
    case 'SET_COLLECTION':
      return { ...state, jsonForm: { ...state.jsonForm, collection: payload } }
    case 'SET_PROPS':
      return { ...state, jsonForm: { ...state.jsonForm, props: payload } }
    case 'SET_MAP_FORM_ID':
      return { ...state, jsonForm: { ...state.jsonForm, mapFormId: payload } }
    case 'SET_MAP_PAGE_ID':
      return { ...state, jsonForm: { ...state.jsonForm, mapPageId: payload } }
    case 'SET_FAVORITES':
      return { ...state, jsonForm: { ...state.jsonForm, favorites: payload } }
    case 'SET_DATASOURCES':
      objectPath.set(state, 'jsonForm.datasources', payload)
      return state
    case 'SET_LAYOUT_HISTORY':
      let newState = state
      let layoutHistory = objectPath.get(newState, 'jsonForm.layoutHistory')
      objectPath.set(newState, 'jsonForm.layoutHistory', {
        ...layoutHistory,
        ...payload,
      })
      return newState
    case 'SET_FORM_REVISION_ID':
      return {
        ...state,
        jsonForm: { ...state.jsonForm, formRevisionId: payload },
      }
    case 'SET_FORM_DESIGN':
      return { ...state, formDesign: payload }
    case 'SET_JSON_FORM':
      return {
        ...state,
        jsonForm:
          typeof payload === 'function' ? payload(state.jsonForm) : payload,
      }
    case 'SET_FORMULAS':
      return {
        ...state,
        jsonForm: {
          ...state.jsonForm,
          formulas: payload,
        },
      }
    case 'SET_RULES':
      return {
        ...state,
        jsonForm: {
          ...state.jsonForm,
          rules: payload,
        },
      }
    case 'SET_MODAL_FORM':
      return { ...state, modalForm: payload }
    case 'SET_CURRENT_DATA':
      return {
        ...state,
        current:
          typeof payload === 'function' ? payload(state.current) : payload,
      }
    case 'SET_EDITOR_KEY':
      return { ...state, editorKey: payload }
    case 'SET_GROUP_KEY':
      return { ...state, groupKey: payload }
    case 'SET_SUB_GROUP':
      return { ...state, subGroup: payload }
    case 'SET_EDITOR_DROP_ID':
      return { ...state, editorDropId: payload }
    case 'SET_OPEN_MODAL':
      return { ...state, openModal: payload }
    case 'SET_OPEN_MODAL_CONDITION':
      return { ...state, openModalCondition: payload }
    case 'SET_NEW_UI':
      return { ...state, newUi: payload }
    case 'SET_TEMP_UI':
      return { ...state, tempUi: payload }
    case 'SET_TEMP_MODAL':
      let tempModal = state.tempModal.includes(payload)
        ? state.tempModal
        : [...state.tempModal, payload]
      return { ...state, tempModal: tempModal }
    case 'SET_TEMP_COMPONENT':
      let tempComponent = state.tempComponent.includes(payload)
        ? state.tempComponent
        : [...state.tempComponent, payload]
      return { ...state, tempComponent: tempComponent }
    case 'SET_MODULES':
      return { ...state, modules: payload }
    case 'SET_CATEGORIES':
      return { ...state, categories: payload }
    case 'SET_SEARCH_DATA':
      return { ...state, searchData: payload }
    case 'SET_FIELD_SEARCH_DATA':
      let searchData = cloneDeep(state.searchData || {})
      if (isDelete) {
        objectPath.del(searchData, key)
      } else {
        setObjectPathValue(searchData, key, payload)
      }
      return { ...state, searchData }
    case 'SET_IS_INPUT_SEARCH':
      return { ...state, isInputSearch: payload }
    case 'SET_SEARCH_RESULT':
      return { ...state, searchResult: payload }
    case 'SET_DATA_GET':
      return { ...state, dataGET: { ...state.dataGET, ...payload } }
    case 'SET_SEND_PROPS':
      return { ...state, sendProps: payload }
    case 'SET_MAP_BIND':
      objectPath.set(state, 'jsonForm.schema.mapBind', payload)
      return state
    case 'SET_SHOW_BIND_DATA':
      return { ...state, showBindData: payload }
    case 'SET_SHOW_BIND_PARAM':
      return { ...state, showBindParam: payload }
    case 'SET_IS_AUTO_SAVE':
      return { ...state, jsonForm: { ...state.jsonForm, isAutoSave: payload } }
    case 'SET_AUTO_SAVE_TIME':
      return {
        ...state,
        jsonForm: { ...state.jsonForm, autoSaveTime: payload },
      }
    case 'SET_UPDATED_AT':
      return { ...state, jsonForm: { ...state.jsonForm, updatedAt: payload } }
    case 'SET_WIDGET_COMPONENT':
      return { ...state, widgetComponent: payload }
    case 'SET_MODAL_COMPONENT':
      return { ...state, modalComponent: payload }
    case 'SET_IS_SYNC_WIDGET':
      return { ...state, isSyncWidget: payload }
    case 'SET_DASHBOARD_LIST':
      return { ...state, dashboardList: payload }
    case 'SET_DASHBOARD_TYPE':
      return { ...state, dashboardType: payload }
    case 'SET_EMPLOYEE_LIST':
      return { ...state, employeeList: payload }
    case 'SET_OPEN_SETTING':
      return { ...state, openSetting: payload }
    case 'SET_ON_FULL_SCREEN':
      return { ...state, fullscreen: payload }
    case 'SET_FILTER_DASHBOARD':
      return { ...state, filterDashboard: payload }
    case 'SET_IS_LOADING_DASHBOARD':
      return { ...state, isLoadingDashboard: payload }
    case 'SET_IS_UI_CHAGED': {
      const nextIsUiChanged =
        typeof payload === 'function' ? payload(state.isUiChanged) : payload
      const safeIsUiChanged = preserveOpenModalState(
        state.isUiChanged,
        nextIsUiChanged,
        state.openModal,
      )
      return {
        ...state,
        isUiChanged: {
          ...safeIsUiChanged,
          activeTab: {
            ...safeIsUiChanged?.activeTab,
            ...state.isUiChanged?.activeTab,
          },
        },
      }
    }
    case 'SET_IS_SELECT_ROW':
      return { ...state, isSelectRow: payload }
    case 'SET_TABLE_REFRESH':
      return { ...state, tableRefresh: payload }
    case 'SET_IS_RESET_DATA':
      return { ...state, isResetData: payload }
    case 'SET_TAB_PARAM_ACTIVE':
      return { ...state, tabParamActive: payload }
    case 'SET_TAB_ACTIVE_STATE':
      return createTabActiveState(state, payload)
    case 'SET_IS_ADD_DYNAMIC':
      return { ...state, isAddDynamic: payload }
    case 'SET_IS_UPDATE_DYNAMIC':
      return { ...state, isUpdateDynamic: payload }
    case 'SET_IS_LOADING':
      return { ...state, isLoading: payload }
    case 'SET_KEYWORD_FILTER':
      return { ...state, keywordFilter: payload }
    case 'SET_ALL_DATA':
      return { ...state, allData: payload }
    case 'SET_FIELD_ALL_DATA':
      objectPath.set(state, `allData.${key}`, payload)
      return state
    case 'SET_CONDITIONS':
      objectPath.set(state, 'jsonForm.conditions', payload)
      return state
    case 'SET_ELEMENT_ACTION':
      return { ...state, elementAction: payload }
    case 'SET_SHOW_INVISIBLE':
      return { ...state, invisible: payload }
    case 'SET_PAGINATION':
      return { ...state, pagination: payload }
    case 'SET_DISPLAY_MODE':
      return { ...state, displayMode: payload }
    case 'SET_DATASOURCE_VALUE':
      return { ...state, '@datasource': payload }
    case 'SET_INIT_OPTIONS':
      return { ...state, isInitalOption: payload }
    case 'SET_IS_CHECKED_CONDITION':
      return { ...state, isCheckedCondition: payload }
    case 'SET_CURRENT_SEARCH':
      return { ...state, currentSearch: payload }
    case 'SET_ACTIVE_BUTTON':
      return { ...state, activeButton: payload }
    case 'SET_WIDGET_JSON_FORM':
      return { ...state, widgetJsonForm: payload }
    case 'SET_CONDITION_PROPS':
      return {
        ...state,
        jsonForm: { ...state.jsonForm, conditionProps: payload },
      }
    case 'SET_CALLBACK':
      return { ...state, callback: payload }
    case 'SET_DESIGNER_EVENT':
      return { ...state, designerEvent: payload }
    case 'SET_IS_LOADING_API':
      return { ...state, isLoadingApi: payload }
    case 'SET_SELECT_API_OPTION':
      return { ...state, selectApiOption: payload }
    case 'SET_ALL_FORM_GROUP':
      return { ...state, allFormGroup: payload }
    case 'SET_ALL_WIDGET_DESIGN':
      return { ...state, widgetDesign: payload }
    case 'SET_DOCUMENT_JSON':
      return { ...state, documentJson: payload }
    default:
      return state
  }
}

const DesignProvider = ({ children }) => {
  const [state, dispatch] = useReducer(DesignReducer, initialState)

  const { jsonForm = {}, current, openModal, modalForm, isUiChanged } = state

  const setSchema = (payload) => dispatch({ type: 'SET_SCHEMA', payload })

  const setMapBind = (payload) => dispatch({ type: 'SET_MAP_BIND', payload })

  const setUischema = (payload) => dispatch({ type: 'SET_UI_SCHEMA', payload })

  const setNewUi = (payload) => dispatch({ type: 'SET_NEW_UI', payload })

  const setTempUi = (payload) => dispatch({ type: 'SET_TEMP_UI', payload })

  const setAllData = (payload) => dispatch({ type: 'SET_ALL_DATA', payload })

  const setFieldAllData = (key, payload) =>
    dispatch({ type: 'SET_FIELD_ALL_DATA', key, payload })

  const setIsInitialOption = (payload) =>
    dispatch({ type: 'SET_INIT_OPTIONS', payload })

  const setIsCheckedCondition = (payload) =>
    dispatch({ type: 'SET_IS_CHECKED_CONDITION', payload })

  const setCurrentSearch = (payload) =>
    dispatch({ type: 'SET_CURRENT_SEARCH', payload })

  const setActiveButton = (payload) =>
    dispatch({ type: 'SET_ACTIVE_BUTTON', payload })

  const setTableRefresh = (payload) =>
    dispatch({ type: 'SET_TABLE_REFRESH', payload })

  const setTabParamActive = (payload) =>
    dispatch({ type: 'SET_TAB_PARAM_ACTIVE', payload })

  const setTabActiveState = (payload) =>
    dispatch({ type: 'SET_TAB_ACTIVE_STATE', payload })

  const setIsResetData = (payload) =>
    dispatch({ type: 'SET_IS_RESET_DATA', payload })

  const setTempModal = (payload) =>
    dispatch({ type: 'SET_TEMP_MODAL', payload })

  const setTempComponent = (payload) =>
    dispatch({ type: 'SET_TEMP_COMPONENT', payload })

  const setFormData = (payload) => dispatch({ type: 'SET_FORM_DATA', payload })

  const clearData = () => dispatch({ type: 'CLEAR_DATA' })

  const setFieldFormData = (key, payload, isDelete = false) => {
    notifyDesignerOnChange({ key, payload, isDelete, state })
    dispatch({ type: 'SET_FIELD_FORM_DATA', key, payload, isDelete })
  }

  const setFieldSearchData = (uischema, key, payload, config = {}) => {
    let { isSearch = false, isDelete = false } = config
    notifyDesignerOnChange({ key, payload, isDelete, state })
    if (isSearch || uischema?.layout === 'header') {
      dispatch({ type: 'SET_FIELD_SEARCH_DATA', key, payload, isDelete })
    } else {
      dispatch({ type: 'SET_FIELD_FORM_DATA', key, payload, isDelete })
    }
  }

  const setIsInputSearch = (payload) =>
    dispatch({ type: 'SET_IS_INPUT_SEARCH', payload })

  const setDesignerPropsData = (payload) =>
    dispatch({ type: 'SET_DESIGNER_PROPS_DATA', payload })

  const setComponentPropsData = (payload) =>
    dispatch({ type: 'SET_COMPONENT_PROPS_DATA', payload })

  const setFormGroup = (payload) =>
    dispatch({ type: 'SET_FORM_GROUP', payload })

  const setBulkGroup = (payload) =>
    dispatch({ type: 'SET_BULK_GROUP', payload })

  const setDataset = (payload) => dispatch({ type: 'SET_DATASET', payload })

  const setApiGroup = (payload) => dispatch({ type: 'SET_API_GROUP', payload })

  const setApiConditionGroup = (payload) =>
    dispatch({ type: 'SET_API_CONDITION_GROUP', payload })

  const setApiSearchTable = (payload) =>
    dispatch({ type: 'SET_API_SEARCH_TABLE', payload })

  const setCustomGroup = (payload) =>
    dispatch({ type: 'SET_CUSTOM_GROUP', payload })

  const setTableGroup = (payload) =>
    dispatch({ type: 'SET_TABLE_GROUP', payload })

  const setJsonForm = (payload) => dispatch({ type: 'SET_JSON_FORM', payload })

  const setFormulas = (payload) => dispatch({ type: 'SET_FORMULAS', payload })

  const setRules = (payload) => dispatch({ type: 'SET_RULES', payload })

  const setModalForm = (payload) =>
    dispatch({ type: 'SET_MODAL_FORM', payload })

  const setFormDesign = (payload) =>
    dispatch({ type: 'SET_FORM_DESIGN', payload })

  const setRequireField = (payload) =>
    dispatch({ type: 'SET_REQUIRE_FIELD', payload })

  const setValidation = (payload) =>
    dispatch({ type: 'SET_VALIDATION', payload })

  const setCollection = (payload) =>
    dispatch({ type: 'SET_COLLECTION', payload })

  const setDatasources = (payload) =>
    dispatch({ type: 'SET_DATASOURCES', payload })

  const setPageFormProps = (payload) => dispatch({ type: 'SET_PROPS', payload })

  const setMapFormId = (payload) =>
    dispatch({ type: 'SET_MAP_FORM_ID', payload })

  const setMapPageId = (payload) =>
    dispatch({ type: 'SET_MAP_PAGE_ID', payload })

  const setLayoutHistory = (payload) =>
    dispatch({ type: 'SET_LAYOUT_HISTORY', payload })

  const setFormRevisionId = (payload) =>
    dispatch({ type: 'SET_FORM_REVISION_ID', payload })

  const resetContext = (payload) => {
    dispatch({ type: 'RESET_CONTEXT', payload })
  }

  const setDataGET = (payload) => {
    dispatch({ type: 'SET_DATA_GET', payload })
  }

  const setSendProps = (payload) => {
    dispatch({ type: 'SET_SEND_PROPS', payload })
  }

  const setShowBindData = (payload) =>
    dispatch({ type: 'SET_SHOW_BIND_DATA', payload })

  const setShowBindParam = (payload) =>
    dispatch({ type: 'SET_SHOW_BIND_PARAM', payload })

  const setIsAutoSave = (payload) =>
    dispatch({ type: 'SET_IS_AUTO_SAVE', payload })

  const setAutoSaveTime = (payload) =>
    dispatch({ type: 'SET_AUTO_SAVE_TIME', payload })

  const setUpdatedAt = (payload) =>
    dispatch({ type: 'SET_UPDATED_AT', payload })

  const setIsClearData = () => dispatch({ type: 'IS_CLEAR_DATA' })

  const setWidgetComponent = (code, jsonForm) => {
    let payload = state.widgetComponent
    objectPath.set(payload, code, jsonForm)
    dispatch({ type: 'SET_WIDGET_COMPONENT', payload })
  }

  const setModalComponent = (code, jsonForm) => {
    let payload = state.modalComponent
    objectPath.set(payload, code, jsonForm)
    dispatch({ type: 'SET_MODAL_COMPONENT', payload })
  }

  const setIsSyncWidget = (payload) => {
    dispatch({ type: 'SET_IS_SYNC_WIDGET', payload })
  }

  const setIsLoadingDashboard = (payload) => {
    dispatch({ type: 'SET_IS_LOADING_DASHBOARD', payload })
  }

  const setIsUiChanged = (payload) => {
    dispatch({ type: 'SET_IS_UI_CHAGED', payload })
  }

  const setIsSelectRow = (payload) =>
    dispatch({ type: 'SET_IS_SELECT_ROW', payload })

  const setOpenSetting = (payload) => {
    dispatch({ type: 'SET_OPEN_SETTING', payload })
  }

  const onFullscreen = () => {
    let payload = !state.fullscreen
    dispatch({ type: 'SET_ON_FULL_SCREEN', payload })
  }

  const setDashboardList = (payload) => {
    dispatch({ type: 'SET_DASHBOARD_LIST', payload })
  }

  const setFilterDashboard = (payload) => {
    dispatch({ type: 'SET_FILTER_DASHBOARD', payload })
  }

  const setFavorites = (payload) => {
    dispatch({ type: 'SET_FAVORITES', payload })
  }

  const setIsAddDynamic = (payload) => {
    dispatch({ type: 'SET_IS_ADD_DYNAMIC', payload })
  }

  const setIsUpdateDynamic = (payload) => {
    dispatch({ type: 'SET_IS_UPDATE_DYNAMIC', payload })
  }

  const setIsLoading = (payload) => {
    dispatch({ type: 'SET_IS_LOADING', payload })
  }

  const setKeywordFilter = (payload) => {
    dispatch({ type: 'SET_KEYWORD_FILTER', payload })
  }

  const setConditions = (payload) =>
    dispatch({ type: 'SET_CONDITIONS', payload })

  const setShowInvisible = (payload) =>
    dispatch({ type: 'SET_SHOW_INVISIBLE', payload })

  const setElementAction = (payload) =>
    dispatch({ type: 'SET_ELEMENT_ACTION', payload })

  const setPagination = (payload) =>
    dispatch({ type: 'SET_PAGINATION', payload })

  const setDisplayMode = (payload) =>
    dispatch({ type: 'SET_DISPLAY_MODE', payload })

  const setDatasourceValue = (payload) =>
    dispatch({ type: 'SET_DATASOURCE_VALUE', payload })

  const setWidgetJsonForm = (payload) =>
    dispatch({ type: 'SET_WIDGET_JSON_FORM', payload })

  const setConditionProps = (payload) =>
    dispatch({ type: 'SET_CONDITION_PROPS', payload })

  const setCallback = (payload) => dispatch({ type: 'SET_CALLBACK', payload })

  const setDesignerEvent = (payload) =>
    dispatch({ type: 'SET_DESIGNER_EVENT', payload })

  const setIsLoadingApi = (payload) =>
    dispatch({ type: 'SET_IS_LOADING_API', payload })

  const setSelectApiOption = (payload) =>
    dispatch({ type: 'SET_SELECT_API_OPTION', payload })

  const setAllFormGroup = (payload) =>
    dispatch({ type: 'SET_ALL_FORM_GROUP', payload })

  const setWidgetDesign = (payload) =>
    dispatch({ type: 'SET_ALL_WIDGET_DESIGN', payload })

  const setDocumentJson = (payload) =>
    dispatch({ type: 'SET_DOCUMENT_JSON', payload })

  const findUischemaIndex = (
    currentKey,
    mapKey,
    elements = jsonForm.uischema.elements,
  ) => {
    let indexList = []
    let newElements = elements
    if (mapKey) {
      let listKey = mapKey.split('.')
      listKey.forEach((key, i, arr) => {
        let index = newElements.findIndex((element) => element.key === key)
        if (index < 0) {
          arr.length = i + 1
          return
        }
        indexList = [...indexList, index]
        let element = objectPath.get(newElements, [index, 'elements'])
        if (element) {
          newElements = element
        }
        if (key === currentKey) {
          arr.length = i + 1
        }
      })
      return indexList
    }
    return []
  }

  const findSchema = (properties, indices) => {
    let newProperties = indices.reduce(
      (o, i) => objectPath.get(o, ['properties', i]),
      {
        properties,
      },
    )
    return newProperties
  }

  const findUiSchema = (elements, indices) => {
    let newElements = indices.reduce(
      (o, i) => objectPath.get(o, ['elements', i]),
      { elements },
    )
    return newElements
  }

  const cloneUiSchemaElementsByIndices = (elements = [], indices = []) => {
    let newElements = [...elements]
    let currentElements = newElements

    indices.forEach((index, i) => {
      let element = currentElements[index]
      let isLastIndex = i === indices.length - 1
      let newElement = isLastIndex
        ? cloneDeep(element)
        : {
            ...element,
            elements: element?.elements ? [...element.elements] : undefined,
          }

      currentElements[index] = newElement
      currentElements = newElement.elements || []
    })

    return newElements
  }

  const getCurrent = (
    key,
    schema = jsonForm.schema,
    uischema = jsonForm.uischema,
  ) => {
    if (!key) {
      return {
        ui: null,
        schema: null,
      }
    }
    let mapKey = objectPath.get(schema, ['mapKey', key]) || ''
    let elements = uischema.elements
    let indices = findUischemaIndex(key, mapKey, elements)
    let currentElement = findUiSchema(elements, indices)
    let mapName = objectPath.get(schema, ['mapName', key]) || ''
    let nameList = mapName.split('.')
    let currentSchema = findSchema(schema.properties, nameList)
    return {
      ui: currentElement,
      schema: currentSchema,
    }
  }

  const getUi = (key, schema, uischema) => {
    let mapKey = objectPath.get(schema, ['mapKey', key]) || ''
    let elements = uischema.elements
    let indices = findUischemaIndex(key, mapKey, elements)
    let currentElement = findUiSchema(elements, indices)
    return currentElement
  }

  const getCurrentUi = (
    key,
    schema = jsonForm.schema,
    uischema = jsonForm.uischema,
  ) => {
    let tabForm = isUiChanged?.tabForm
    if (tabForm) {
      let tabFormUi
      if (tabForm) {
        Object.values(tabForm).forEach(({ jsonForm }) => {
          let ui = getUi(key, jsonForm.schema, jsonForm.uischema)
          if (ui.key === key) {
            tabFormUi = ui
          }
        })
      }
      if (tabFormUi) return tabFormUi
    }
    return getUi(key, schema, uischema)
  }

  const deleteSchema = (mapName, schema) => {
    let properties = jsonForm.schema.properties
    let indices = mapName.split('.')
    let lastKey = indices.pop()
    if (indices.length > 0) {
      let newElements = indices.reduce(
        (o, i) => objectPath.get(o, ['properties', i]),
        {
          properties,
        },
      )?.properties
      if (objectPath.has(newElements, lastKey)) {
        delete newElements[lastKey]
      }
    } else {
      if (objectPath.has(properties, lastKey)) {
        delete properties[lastKey]
      }
    }
    schema.properties = properties
    setSchema(schema)
  }

  const deleteUischema = (indices, uischema) => {
    if (!indices?.length) return uischema
    let elements = jsonForm.uischema.elements
    let lastIndex = indices.pop()
    let newElements = indices.reduce(
      (o, i) => objectPath.get(o, ['elements', i]),
      {
        elements,
      },
    )?.elements
    let currentUi = newElements[lastIndex]
    if (!currentUi) return uischema

    if (currentUi.tagname === 'widget') {
      setWidgetComponent(currentUi.key, jsonForm, false)
    } else if (currentUi.tagname === 'component') {
      let designerPropsData =
        objectPath.get(jsonForm, 'designerPropsData') || {}
      let receiveProps = objectPath.get(designerPropsData, `receive`) || []
      let foundIndex = receiveProps.findIndex(
        ({ propKey }) => currentUi.key === propKey,
      )
      if (foundIndex > -1) {
        objectPath.del(receiveProps, foundIndex)
      }
      objectPath.set(designerPropsData, 'receive', receiveProps)
      setDesignerPropsData(designerPropsData)
    }

    if (lastIndex > -1) {
      newElements.splice(lastIndex, 1)
    }
    let newUiSchema = { ...uischema, elements: cloneDeep(elements) }
    setUischema(newUiSchema)
    return newUiSchema
  }

  const deleteMapData = (key, schema) => {
    let newMapKey = schema?.mapKey || {}
    let collection = jsonForm.collection
    let mapKeyList = Object.keys(newMapKey)
    let mapKey = mapKeyList.filter((value) => newMapKey[value].search(key) > -1)
    mapKey.forEach((subKey) => {
      if (schema.mapName) delete schema.mapName[subKey]
      if (schema.mapKey) delete schema.mapKey[subKey]
      if (schema.mapTag) delete schema.mapTag[subKey]
      if (schema.mapField) delete schema.mapField[subKey]
      if (schema.mapFormData) delete schema.mapFormData[subKey]
      if (schema.mapWidget) delete schema.mapWidget[subKey]
      if (schema.mapBind) delete schema.mapBind[subKey]
      if (schema.mapDynamic) delete schema.mapDynamic[subKey]
      if (collection) delete deleteCollection(subKey, collection)
    })
    setSchema(schema)
  }

  const deleteCustomGroup = (key) => {
    let newCustomGroup = jsonForm?.customGroup || []
    let filter = newCustomGroup.filter((value) => value.key !== key)
    setCustomGroup(filter)
  }

  const deleteCollection = (key, collection) => {
    objectPath.del(collection, `jsonKey.${key}`)
    objectPath.del(collection, `jsonName.${key}`)
    objectPath.del(collection, `map.${key}`)
    setCollection(collection)
  }

  const deleteSearch = (key) => {
    let newJsonForm = jsonForm
    let schema = jsonForm.schema
    let field = objectPath.get(newJsonForm, `search.field`) || []
    let filter = field.filter((item, index) => {
      if (item.groupKey === key) return false
      if (item.key === key) return false
      return true
    })
    objectPath.set(newJsonForm, `search.field`, filter)
    setJsonForm(newJsonForm)
  }

  const deleteCondition = (uiKey) => {
    const conditionList = objectPath.get(jsonForm, 'conditions') || []
    conditionList.forEach(
      ({ actions = [], conditions = [], isIfElse }, index) => {
        if (isIfElse) {
        } else {
          let filterConditions = conditions.filter(({ key }) => key !== uiKey)
          if (filterConditions.length === 0) {
            objectPath.del(conditionList, index)
          } else {
            let filterActions = actions.filter(({ key }) => key !== uiKey)
            objectPath.set(
              conditionList,
              [index, 'conditions'],
              filterConditions,
            )
            objectPath.set(conditionList, [index, 'actions'], filterActions)
          }
        }
      },
    )
    setConditions(conditionList)
  }

  const findIndicesFromSubElements = (
    layoutElements,
    subElements,
    key,
    subIndices,
    index,
  ) => {
    let element = objectPath.get(subElements, index)
    if (!element) return subIndices
    if (element.key === key) {
      subIndices.push(index)
      return subIndices
    }
    let elements = objectPath.get(element, 'elements') || []
    let noElements = elements.length === 0
    if (noElements) {
      subIndices = []
      index = index + 1
      return findIndicesFromSubElements(
        layoutElements,
        layoutElements,
        key,
        subIndices,
        index,
      )
    }
    index = index + 1
    return findIndicesFromSubElements(
      layoutElements,
      elements,
      key,
      subIndices,
      index,
    )
  }

  const deleteUi = (ui, path, key) => {
    const ensuredData = ensureMapByUiKey(ui)
    let mapKey = ensuredData.mapKey
    let mapName = ensuredData.mapName
    let schema = ensuredData.schema
    let uischema = jsonForm.uischema
    let indices = findUischemaIndex(key, mapKey)
    let currentUi = findUiSchema(jsonForm.uischema.elements, indices)
    if (currentUi?.key !== key) {
      indices = buildUiIndexPathByKey(jsonForm.uischema.elements, key)
    }
    if (mapName) {
      deleteSchema(mapName, schema)
      deleteMapData(key, schema)
    }
    let newUiSchema = deleteUischema(indices, uischema)
    deleteCustomGroup(key)
    deleteSearch(key)
    deleteCondition(key)
    let layout = ui.layout
    let newCollection = jsonForm?.collection || {}
    objectPath.set(newCollection, `version.${layout}`, 0)
    setCollection(newCollection)
    deleteDesignerPropsData(key)
    let formData = jsonForm.formData
    objectPath.del(formData, key)
    setFormData(formData)
    setJsonForm({
      ...jsonForm,
      schema,
      uischema: newUiSchema,
      formData,
    })
    setNewUi({ delete: true, key })
    if (current?.ui?.key === key) {
      setCurrent({
        ui: newUiSchema,
        schema,
        readOnly: false,
      })
      setEditorDropId('')
      setEditorKey('')
    }
  }

  const deleteDesignerPropsData = (key) => {
    let designerPropsData = objectPath.get(state, 'jsonForm.designerPropsData')
    objectPath.del(designerPropsData, `data.${key}`)
    objectPath.del(designerPropsData, `widgetCode.${key}`)
    objectPath.del(designerPropsData, `formCode.${key}`)
    objectPath.del(designerPropsData, `pageCode.${key}`)
    let list = { ...designerPropsData?.map, ...designerPropsData?.sendType }
    Object.keys(list).forEach((mapKey) => {
      if (mapKey.search(key) > -1) {
        objectPath.del(designerPropsData, `map.${mapKey}`)
        objectPath.del(designerPropsData, `api.${mapKey}`)
        objectPath.del(designerPropsData, `sendType.${mapKey}`)
        objectPath.del(designerPropsData, `receiveType.${mapKey}`)
        objectPath.del(designerPropsData, `propType.${mapKey}`)
      }
    })
    setDesignerPropsData(designerPropsData)
  }

  const buildUiPathByKey = (elements = [], targetKey, parentPath = []) => {
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i]
      if (!element?.key) continue
      const currentPath = [...parentPath, element.key]
      if (element.key === targetKey) {
        return currentPath
      }
      const childElements = element?.elements || []
      if (childElements.length > 0) {
        const childPath = buildUiPathByKey(
          childElements,
          targetKey,
          currentPath,
        )
        if (childPath.length > 0) return childPath
      }
    }
    return []
  }

  const buildUiIndexPathByKey = (elements = [], targetKey, parentPath = []) => {
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i]
      if (!element?.key) continue
      const currentPath = [...parentPath, i]
      if (element.key === targetKey) {
        return currentPath
      }
      const childElements = element?.elements || []
      if (childElements.length > 0) {
        const childPath = buildUiIndexPathByKey(
          childElements,
          targetKey,
          currentPath,
        )
        if (childPath.length > 0) return childPath
      }
    }
    return []
  }

  const findUiByKey = (elements = [], targetKey) => {
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i]
      if (element?.key === targetKey) return element

      const childElements = element?.elements || []
      if (childElements.length > 0) {
        const childElement = findUiByKey(childElements, targetKey)
        if (childElement) return childElement
      }
    }

    return null
  }

  const getSchemaByElementTag = (uiTag, currentSchema = {}) => {
    const schema = { ...currentSchema }
    if (!uiTag) return schema
    const convertEntries = Object.entries(convertElementSchema)
      .filter(([, elementType]) => elementType === uiTag)
      .map(([schemaType]) => schemaType)

    if (convertEntries.length === 0) return schema

    const hasType = (type) => convertEntries.includes(type)
    if (hasType('number')) {
      schema.type = 'number'
      delete schema.format
      return schema
    }
    if (hasType('boolean')) {
      schema.type = 'boolean'
      delete schema.format
      return schema
    }
    if (hasType('array')) {
      schema.type = 'array'
      delete schema.format
      return schema
    }
    if (hasType('date') || hasType('date-time') || hasType('datetime')) {
      schema.type = 'string'
      schema.format = hasType('date') ? 'date' : 'date-time'
      return schema
    }
    if (hasType('tile')) {
      schema.type = 'string'
      schema.format = 'time'
      return schema
    }
    if (hasType('data-url')) {
      schema.type = 'string'
      schema.format = 'data-url'
      return schema
    }
    if (hasType('email') || hasType('uri')) {
      schema.type = 'string'
      schema.format = hasType('email') ? 'email' : 'uri'
      return schema
    }
    schema.type = 'string'
    delete schema.format
    return schema
  }

  const ensureMapKeyByUiKey = (ui = {}) => {
    const uiKey = ui?.key
    if (!uiKey) return { schema: jsonForm.schema, mapKey: '' }

    let schema = jsonForm.schema || {}
    schema.mapKey = schema.mapKey || {}

    const uiPath = buildUiPathByKey(jsonForm?.uischema?.elements || [], uiKey)
    const recoveredMapKey = uiPath.join('.')
    if (recoveredMapKey) {
      schema.mapKey[uiKey] = recoveredMapKey
    }
    return {
      schema,
      mapKey: schema.mapKey[uiKey] || '',
    }
  }

  const ensureMapNameByUiKey = (ui = {}, config = {}) => {
    const { strictName = false } = config
    const uiKey = ui?.key
    if (!uiKey) return { schema: jsonForm.schema, mapName: '' }

    let schema = jsonForm.schema || {}
    schema.mapName = schema.mapName || {}

    const uiPath = buildUiPathByKey(jsonForm?.uischema?.elements || [], uiKey)

    const pathLength = uiPath.length
    const parentKey = pathLength > 1 ? uiPath[pathLength - 2] : ''
    const parentMapName = parentKey ? schema.mapName?.[parentKey] : ''
    const currentMapName = schema.mapName?.[uiKey] || ''
    const currentNamePath = currentMapName ? currentMapName.split('.') : []
    const currentLeafName = currentNamePath[currentNamePath.length - 1] || ''
    const hasUiName = typeof ui?.name === 'string' && ui.name !== ''
    const leafName = hasUiName ? ui.name : uiKey

    let mapName = currentMapName
    if (strictName && !hasUiName && !currentMapName) {
      return {
        schema,
        mapName: '',
      }
    }
    const shouldRebuildMapName =
      !currentMapName ||
      (hasUiName && currentLeafName && ui.name !== currentLeafName)
    if (shouldRebuildMapName) {
      mapName = parentMapName ? `${parentMapName}.${leafName}` : leafName
      schema.mapName[uiKey] = mapName
    }

    if (!mapName && (!strictName || hasUiName)) {
      mapName = leafName
      schema.mapName[uiKey] = mapName
    }
    return {
      schema,
      mapName: schema.mapName[uiKey] || '',
    }
  }

  const ensureMapAndSchemaByUiKey = (ui = {}) => {
    const uiKey = ui?.key
    if (!uiKey) return { schema: jsonForm.schema, mapKey: '', mapName: '' }

    let schema = jsonForm.schema || {}
    schema.mapKey = schema.mapKey || {}
    schema.mapName = schema.mapName || {}
    schema.mapField = schema.mapField || {}
    schema.mapTag = schema.mapTag || {}
    schema.mapFormData = schema.mapFormData || {}
    schema.properties = schema.properties || {}

    const uiPath = buildUiPathByKey(jsonForm?.uischema?.elements || [], uiKey)
    const mapKey = uiPath.join('.')
    if (mapKey) {
      schema.mapKey[uiKey] = mapKey
    }

    const pathLength = uiPath.length
    const parentKey = pathLength > 1 ? uiPath[pathLength - 2] : ''
    const parentMapName = parentKey ? schema.mapName?.[parentKey] : ''
    const currentMapName = schema.mapName?.[uiKey] || ''
    const currentNamePath = currentMapName ? currentMapName.split('.') : []
    const currentLeafName = currentNamePath[currentNamePath.length - 1] || ''
    const leafName = ui?.name || uiKey

    let mapName = currentMapName
    const shouldRebuildMapName =
      !currentMapName ||
      (ui?.name && currentLeafName && ui.name !== currentLeafName)

    if (shouldRebuildMapName) {
      mapName = parentMapName ? `${parentMapName}.${leafName}` : leafName
      schema.mapName[uiKey] = mapName
    }

    if (!mapName) {
      mapName = leafName
      schema.mapName[uiKey] = mapName
    }

    const nameList = mapName.split('.').filter(Boolean)
    let cursor = { properties: schema.properties }
    nameList.forEach((name, index) => {
      const isLeaf = index === nameList.length - 1
      let node = objectPath.get(cursor, ['properties', name])

      if (!node) {
        node = isLeaf ? {} : { type: 'object', properties: {} }
      }

      if (isLeaf) {
        const recoveredSchema = getSchemaByElementTag(ui?.tagname, node)
        const nextSchema = {
          ...node,
          ...recoveredSchema,
        }
        if (nextSchema.type === 'object') {
          nextSchema.properties = nextSchema.properties || {}
        }
        objectPath.set(cursor, ['properties', name], nextSchema)
      } else {
        if (node.type !== 'object') {
          node.type = 'object'
        }
        node.properties = node.properties || {}
        objectPath.set(cursor, ['properties', name], node)
        cursor = node
      }
    })

    schema.mapField[uiKey] = ui?.fieldName || schema.mapField[uiKey] || uiKey
    schema.mapTag[uiKey] = ui?.tagname || schema.mapTag[uiKey] || ''
    schema.mapFormData[uiKey] =
      ui?.formData ?? schema.mapFormData[uiKey] ?? true
    setSchema(schema)
    return {
      schema,
      mapKey: schema.mapKey[uiKey],
      mapName: schema.mapName[uiKey],
    }
  }

  const ensureMapByUiKey = (ui = {}, config = {}) => {
    const mapKeyData = ensureMapKeyByUiKey(ui)
    const mapNameData = ensureMapNameByUiKey(ui, config)

    return {
      schema: mapNameData.schema,
      mapKey: mapKeyData.mapKey || '',
      mapName: mapNameData.mapName || '',
    }
  }

  const editUi = (uischema, config = {}) => {
    const ensuredData = ensureMapAndSchemaByUiKey(uischema)
    applyUiDefaults(uischema)
    let mapKey = ensuredData.mapKey
    let mapName = ensuredData.mapName
    let nameList = mapName.split('.')
    let newSchema = findSchema(ensuredData.schema.properties, nameList)
    setUischema({ ...jsonForm.uischema })
    setCurrent({
      ui: uischema,
      schema: newSchema,
      readOnly: false,
      config: config,
    })
    setSchema(ensuredData.schema)
    setEditorKey(mapKey)
    setEditorDropId(uischema.key)
    setGroupKey(uischema.groupKey)
    setSubGroup(uischema.subGroup)
  }

  const addUiSchema = (elements, destinationIndex, indices, value) => {
    let newElements = findUiSchema(elements, indices)
    newElements = newElements.elements
    let insertIndex = Math.max(
      0,
      Math.min(destinationIndex, newElements.length),
    )
    newElements.splice(insertIndex, 0, value)
    newElements.map((value, i) => (value.index = i))
    return newElements
  }

  const addSchema = (properties, indices, value) => {
    if (indices.length > 0) {
      let lastKey = indices.pop()
      if (!objectPath.has(properties, lastKey)) {
        objectPath.set(properties, lastKey, { type: 'object', properties: {} })
      }
      let newProperties = findSchema(properties, indices) || {}
      if (objectPath.get(newProperties, `properties.${lastKey}.properties`)) {
        let oldProp = objectPath.get(
          newProperties,
          `properties.${lastKey}.properties`,
        )
        objectPath.set(newProperties, `properties.${lastKey}.properties`, {
          ...oldProp,
          ...value,
        })
      } else {
        objectPath.set(newProperties, `properties.${lastKey}`, {
          type: 'object',
          properties: {
            ...value,
          },
        })
      }
      let { header, body, footer } = properties
      let newProps = {}
      if (header) {
        objectPath.set(newProps, 'header', header)
      }
      if (body) {
        objectPath.set(newProps, 'body', body)
      }
      if (footer) {
        objectPath.set(newProps, 'footer', footer)
      }
      properties = newProps
    }
  }

  const saveUi = () => {
    setCurrent({
      ui: jsonForm.uischema,
      schema: jsonForm.schema,
      readOnly: false,
    })
    setEditorDropId('')
    setEditorKey('')
    setGroupKey([])
    setGroupKey(false)
  }

  const closeUi = (path) => {
    updateUiSchema(path, 'isShowUi', false)
  }

  const updateUiSchema = (path, key, value) => {
    if (!jsonForm.schema.mapKey) return
    let mapKey = jsonForm.schema.mapKey[path]
    setUischema((prevUiSchema) => {
      let indices = findUischemaIndex(path, mapKey, prevUiSchema.elements)
      let currentUi = findUiSchema(prevUiSchema.elements, indices)
      if (currentUi?.key !== path) {
        indices = buildUiIndexPathByKey(prevUiSchema.elements, path)
      }
      if (!indices?.length) return prevUiSchema
      let newUiSchema = {
        ...prevUiSchema,
        elements: cloneUiSchemaElementsByIndices(
          prevUiSchema.elements,
          indices,
        ),
      }
      let elements = newUiSchema.elements
      let newElement = indices.reduce((o, i) => o.elements[i], { elements })
      objectPath.set(newElement, key, value)
      return newUiSchema
    })
    if (current?.ui?.key === path) {
      setCurrent((prevCurrent) => {
        if (prevCurrent?.ui?.key !== path) return prevCurrent
        let nextUi = cloneDeep(prevCurrent.ui)
        objectPath.set(nextUi, key, value)
        return {
          ...prevCurrent,
          ui: nextUi,
        }
      })
    }
  }

  const setCurrent = (payload) =>
    dispatch({ type: 'SET_CURRENT_DATA', payload })

  const setEditorKey = (payload) =>
    dispatch({ type: 'SET_EDITOR_KEY', payload })

  const setGroupKey = (payload) => dispatch({ type: 'SET_GROUP_KEY', payload })

  const setSubGroup = (payload) => dispatch({ type: 'SET_SUB_GROUP', payload })

  const setEditorDropId = (payload) =>
    dispatch({ type: 'SET_EDITOR_DROP_ID', payload })

  const setOpenModal = (payload) =>
    dispatch({ type: 'SET_OPEN_MODAL', payload })

  const setOpenModalCondition = (payload) =>
    dispatch({ type: 'SET_OPEN_MODAL_CONDITION', payload })

  const generateString = (length) => {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }

    return result
  }

  const createMapForWidget = (widget, uischema) => {
    let schema = jsonForm.schema
    let mapWidget = jsonForm.schema.mapWidget ?? {}
    Object.keys(widget).forEach((key) => {
      let mapWidget = objectPath.get(mapWidget, [uischema.key, key])
      if (mapWidget) return
      let newKey = key + generateString(5)
      schema.mapName[newKey] = jsonForm.schema.mapName[uischema.key] + '.' + key
      schema.mapKey[newKey] =
        jsonForm.schema.mapKey[uischema.key] + '.' + newKey
      schema.mapField[newKey] = key
      schema.mapFormData[newKey] = true
      mapWidget[uischema.key] = { ...mapWidget[uischema.key], [key]: newKey }
    })
    schema.mapWidget = mapWidget
    setSchema(schema)
  }

  const getModules = async () => {
    let api = `/designer/form-module?isEnabled=1&page=1&perPage=100`
    let payload = await httpClient
      .get(api)
      .then(({ data }) => data?.data)
      .catch(() => [])
    dispatch({ type: 'SET_MODULES', payload })
    return payload
  }

  const setSearchData = (payload) =>
    dispatch({ type: 'SET_SEARCH_DATA', payload })

  const setSearchResult = (payload) =>
    dispatch({ type: 'SET_SEARCH_RESULT', payload })

  let uiElements = objectPath.get(jsonForm, 'uischema.elements') || []
  let schemaProps = objectPath.get(jsonForm, 'schema.properties') || {}

  const getLayoutIndices = (layoutKey) => {
    let layoutIndices = findUischemaIndex(layoutKey, layoutKey)
    return layoutIndices
  }

  const addUiForLayout = (result, { dataUi, dataSchema }) => {
    let layoutKey = objectPath.get(result, 'destination.droppableId')
    let layoutIndex = objectPath.get(result, 'destination.index')
    let layoutIndices = getLayoutIndices(layoutKey)
    objectPath.del(dataUi, 'part')
    addUiSchema(uiElements, layoutIndex, layoutIndices, dataUi)
    addSchema(schemaProps, [layoutKey], dataSchema)
    createSchema(schemaProps, dataUi, layoutKey, layoutKey)
    return dataUi.key
  }

  const createFallbackDynamicPanel = (uischema, tagname) => {
    let key = `${uischema.key}${tagname}`
    let name = `${uischema.name}${tagname}`

    return {
      ...uischema,
      key,
      name,
      tagname,
      type: 'Group',
      uiType: 'container',
      scope: '#/properties/' + name + '/' + tagname,
      elements: [],
    }
  }

  const ensureFallbackDynamicPanel = (containerKey) => {
    let panelTag = ['dynamicPanel', 'editPanel'].find((tag) =>
      containerKey?.endsWith(tag),
    )
    if (!panelTag) return null

    let dynamicKey = containerKey.slice(0, -panelTag.length)
    let dynamicUi = findUiByKey(jsonForm?.uischema?.elements || [], dynamicKey)
    if (!dynamicUi || dynamicUi.tagname !== 'dynamic') return null

    let dynamicMapKey = jsonForm.schema.mapKey?.[dynamicKey]
    let dynamicMapName = jsonForm.schema.mapName?.[dynamicKey]
    let dynamicIndices = findUischemaIndex(dynamicKey, dynamicMapKey)
    if (!dynamicMapKey || !dynamicMapName || !dynamicIndices.length) return null

    let dynamicContainer = findUiSchema(uiElements, dynamicIndices)
    if (!dynamicContainer) return null

    dynamicContainer.elements = Array.isArray(dynamicContainer.elements)
      ? dynamicContainer.elements
      : []

    let schema = jsonForm.schema
    schema.mapKey = schema.mapKey || {}
    schema.mapName = schema.mapName || {}
    schema.mapField = schema.mapField || {}
    schema.mapTag = schema.mapTag || {}
    schema.mapFormData = schema.mapFormData || {}
    schema.mapDynamic = schema.mapDynamic || {}
    schema.mapDynamic[dynamicUi.key] = true

    ;['dynamicPanel', 'editPanel'].forEach((tagname) => {
      let panelKey = `${dynamicUi.key}${tagname}`
      let panelUi =
        dynamicContainer.elements.find((element) => element?.key === panelKey) ||
        createFallbackDynamicPanel(dynamicUi, tagname)

      if (
        !dynamicContainer.elements.find((element) => element?.key === panelKey)
      ) {
        dynamicContainer.elements.push(panelUi)
      }

      let panelMapKey = `${dynamicMapKey}.${panelUi.key}`
      let panelMapName = `${dynamicMapName}.${panelUi.name}`
      let panelSchema = findSchema(schemaProps, dynamicMapName.split('.'))
      panelSchema.type = panelSchema.type || 'object'
      panelSchema.properties = panelSchema.properties || {}
      panelSchema.properties[panelUi.name] =
        panelSchema.properties[panelUi.name] || {
          type: 'object',
          properties: {},
        }
      schema.mapKey[panelUi.key] = panelMapKey
      schema.mapName[panelUi.key] = panelMapName
      schema.mapField[panelUi.key] = panelUi.fieldName || ''
      schema.mapTag[panelUi.key] = panelUi.tagname
      schema.mapFormData[panelUi.key] = panelUi.formData
    })

    return findUiByKey(dynamicContainer.elements, containerKey)
  }

  const addUiForContainer = (result, { dataUi, dataSchema }) => {
    let containerKey = objectPath.get(result, 'destination.droppableId')
    let containerIndex = objectPath.get(result, 'destination.index')
    let containerUi =
      findUiByKey(jsonForm?.uischema?.elements || [], containerKey) ||
      ensureFallbackDynamicPanel(containerKey) ||
      {
        key: containerKey,
      }
    let ensuredData = ensureMapAndSchemaByUiKey(containerUi)
    let mapKey = ensuredData.mapKey || jsonForm.schema.mapKey[containerKey]
    let mapName = ensuredData.mapName || jsonForm.schema.mapName[containerKey]
    let containerIndices = findUischemaIndex(containerKey, mapKey)
    if (!containerIndices.length || !mapName) return
    objectPath.del(dataUi, 'part')
    addUiSchema(uiElements, containerIndex, containerIndices, dataUi)
    addSchema(schemaProps, mapName.split('.'), dataSchema)
    createSchema(schemaProps, dataUi, mapKey, mapName)
    setNewUi({ create: true, key: dataUi.key })
    return dataUi.key
  }

  const createSchema = (schemaProps, dataUi, mapKey, mapName) => {
    let newJsonForm = cloneDeep(jsonForm)
    let { key, name, fieldName, tagname, formData } = dataUi
    objectPath.set(newJsonForm, `schema.properties`, schemaProps)
    objectPath.set(newJsonForm, `schema.mapKey.${key}`, `${mapKey}.${key}`)
    objectPath.set(newJsonForm, `schema.mapName.${key}`, `${mapName}.${name}`)
    objectPath.set(newJsonForm, `schema.mapField.${key}`, fieldName)
    objectPath.set(newJsonForm, `schema.mapTag.${key}`, tagname)
    objectPath.set(newJsonForm, `schema.mapFormData.${key}`, formData)
    setJsonForm(newJsonForm)
  }

  const getDataUi = (layout, result, found, subGroup = false) => {
    const { destination } = result

    if (!found) return
    let scope = found?.scope ?? found.id
    let newKey = found.id + generateString(5)
    // Deep clone template uischema so nested objects (e.g. button `action`)
    // are not shared across elements / with the palette singleton template.
    let dataUi = {
      ...cloneDeep(found.uischema),
      index: destination.index,
      tagname: found.id,
      subGroup: subGroup,
      layout: layout,
      fieldName: found.uischema.fieldName + ' Field',
      scope: '#/properties/' + newKey + '/' + scope,
      key: newKey,
      name: newKey,
      isShowUi: true,
      isShowField: true,
      elements: [],
    }
    if (dataUi.modal) {
      dataUi.isOpen = false
    }
    let dataSchema = {
      [newKey]: {
        type: found.schema.type,
      },
    }
    if (found.schema.type === 'object') {
      dataSchema[newKey] = { ...dataSchema[newKey], properties: {} }
    }
    return { dataUi: dataUi, dataSchema: dataSchema }
  }

  const addInsideModal = (
    found,
    mapKey,
    divKey,
    newKey,
    modalKey = '',
    subGroup = false,
  ) => {
    let dataUi = {
      ...cloneDeep(found.uischema),
      index: 1,
      tagname: found.id,
      subGroup: false,
      fieldName: found.uischema.fieldName + ' Field',
      scope: '#/properties/' + newKey + '/' + found.id,
      key: newKey,
      name: newKey,
      modal: modalKey,
      isShowUi: true,
      isShowField: true,
      elements: [],
    }
    let foundIndex = findUischemaIndex(divKey, mapKey)
    if (!foundIndex) return
    addUiSchema(uiElements, 1, foundIndex, dataUi)
    let mapName = jsonForm.schema.mapName[divKey]
    let dataSchema = {}
    if (found.schema.type === 'object') {
      dataSchema = {
        [newKey]: {
          type: found.schema.type,
          properties: {},
        },
      }
    } else {
      dataSchema = {
        [newKey]: {
          type: found.schema.type,
        },
      }
    }
    addSchema(schemaProps, mapName.split('.'), dataSchema)
    let newSchema = jsonForm.schema
    let newUi = jsonForm.uischema
    newUi.elements = uiElements
    newSchema.properties = schemaProps
    newSchema.mapKey = {
      ...newSchema.mapKey,
      [newKey]: [mapKey, newKey].join('.'),
    }
    newSchema.mapName = {
      ...newSchema.mapName,
      [newKey]: [mapName, newKey].join('.'),
    }
    newSchema.mapTag = { ...newSchema.mapTag, [newKey]: found.id }
    newSchema.mapFormData = {
      ...newSchema.mapFormData,
      [newKey]: found.formData,
    }
    newSchema.mapField = { ...newSchema.mapField, [newKey]: '' }
  }

  const addInsideTab = (found, mapKey, divKey, newKey) => {
    let dataUi = {
      ...cloneDeep(found.uischema),
      index: 1,
      tagname: found.id,
      subGroup: false,
      fieldName: found.uischema.fieldName + ' Field',
      scope: '#/properties/' + newKey + '/' + found.id,
      key: newKey,
      name: newKey,
      isShowUi: true,
      isShowField: true,
      elements: [],
    }
    let dataSchema = {
      [newKey]: {
        type: found.schema.type,
        properties: {},
      },
    }
    let foundIndex = findUischemaIndex(divKey, mapKey)
    if (!foundIndex) return
    addUiSchema(uiElements, 1, foundIndex, dataUi)
    let mapName = jsonForm.schema.mapName[divKey]

    addSchema(schemaProps, mapName.split('.'), dataSchema)
    let newSchema = jsonForm.schema
    let newUi = jsonForm.uischema
    newUi.elements = uiElements
    newSchema.properties = schemaProps
    newSchema.mapKey = {
      ...newSchema.mapKey,
      [newKey]: [mapKey, newKey].join('.'),
    }
    newSchema.mapName = {
      ...newSchema.mapName,
      [newKey]: [mapName, newKey].join('.'),
    }
    newSchema.mapTag = { ...newSchema.mapTag, [newKey]: found.id }
    newSchema.mapFormData = {
      ...newSchema.mapFormData,
      [newKey]: found.formData,
    }
    newSchema.mapField = { ...newSchema.mapField, [newKey]: '' }
  }

  const addInsideDynamic = (found, mapKey, divKey, newKey) => {
    let dataUi = {
      ...cloneDeep(found.uischema),
      index: 1,
      tagname: found.id,
      subGroup: false,
      fieldName: found.uischema.fieldName + ' Field',
      scope: '#/properties/' + newKey + '/' + found.id,
      key: newKey,
      name: newKey,
      isShowUi: true,
      isShowField: true,
      elements: [],
    }
    let dataSchema = {
      [newKey]: {
        type: found.schema.type,
        properties: {},
      },
    }
    let foundIndex = findUischemaIndex(divKey, mapKey)
    if (!foundIndex) return
    addUiSchema(uiElements, 1, foundIndex, dataUi)
    let mapName = jsonForm.schema.mapName[divKey]

    addSchema(schemaProps, mapName.split('.'), dataSchema)
    let newSchema = jsonForm.schema
    let newUi = jsonForm.uischema
    newUi.elements = uiElements
    newSchema.properties = schemaProps
    newSchema.mapKey = {
      ...newSchema.mapKey,
      [newKey]: [mapKey, newKey].join('.'),
    }
    newSchema.mapName = {
      ...newSchema.mapName,
      [newKey]: [mapName, newKey].join('.'),
    }
    newSchema.mapTag = { ...newSchema.mapTag, [newKey]: found.id }
    newSchema.mapFormData = {
      ...newSchema.mapFormData,
      [newKey]: found.formData,
    }
    newSchema.mapField = { ...newSchema.mapField, [newKey]: '' }
  }

  const reorderUiSchema = ({ source, draggableId, destination }) => {
    if (!destination) return
    let elements = jsonForm.uischema.elements

    // prepare uischema
    let sourceKey = draggableId
    let ensuredData = ensureMapByUiKey({ key: sourceKey })
    let sourceMapKey = ensuredData.mapKey

    let indices = findUischemaIndex(sourceKey, sourceMapKey, elements)
    if (!indices.length) return

    indices.pop()
    let newElements = findUiSchema(elements, indices)
    newElements = newElements.elements
    if (!Array.isArray(newElements)) return

    // reorder uischema
    let sourceIndex = newElements.findIndex(({ key }) => key === draggableId)
    if (sourceIndex < 0) return
    let [reorderedItem] = newElements.splice(sourceIndex, 1)
    if (!reorderedItem) return

    let destinationIndex = Math.max(
      0,
      Math.min(destination.index, newElements.length),
    )
    if (destinationIndex < 0) return
    newElements.splice(destinationIndex, 0, reorderedItem)
    newElements.map((value, i) => (value.index = i))

    // set new uischema
    let newUi = { ...jsonForm.uischema, elements: cloneDeep(elements) }
    setJsonForm({ ...jsonForm, uischema: newUi })
    setNewUi({
      move: true,
      source: source.droppableId,
      key: draggableId,
      destination: destination.droppableId,
    })
  }

  const moveUiSchema = ({ source, destination, draggableId }) => {
    let elements = jsonForm.uischema.elements
    let newUi = jsonForm.uischema
    // prepare source uischema
    let sourceIndex = source.index
    if (sourceIndex < 0) return
    let sourceKey = source.droppableId
    let ensuredData = ensureMapByUiKey({ key: sourceKey })
    let sourceMapKey = ensuredData.mapKey
    let sourceIndices = findUischemaIndex(
      sourceKey,
      sourceMapKey,
      jsonForm.uischema.elements,
    )
    let sourceElements = findUiSchema(elements, sourceIndices)
    if (!sourceElements) return

    // prepare move uischema
    let moveIndex = sourceElements.elements.findIndex(
      ({ key }) => draggableId === key,
    )
    if (moveIndex < 0) return
    let moveElement = sourceElements.elements[moveIndex]
    // remove move uischema from source
    sourceElements.elements.splice(moveIndex, 1)
    sourceElements.elements.map((value, i) => (value.index = i))

    // prepare destination uischema
    let destinationKey = destination.droppableId
    let destinationEnsuredData = ensureMapKeyByUiKey({ key: destinationKey })
    let destinationMapKey = destinationEnsuredData.mapKey
    let destinationIndices = findUischemaIndex(
      destinationKey,
      destinationMapKey,
      jsonForm.uischema.elements,
    )
    if (!destinationIndices.length) return

    // find container subGroup for head container
    let headGroup = true
    let destinationElements = destinationIndices.reduce(
      (o, i) => {
        headGroup = o.elements[i].subGroup
        return o.elements[i]
      },
      { elements },
    )

    destinationElements = destinationElements.elements
    // add move uischema to destination
    let destinationIndex = Math.max(
      0,
      Math.min(destination.index, destinationElements.length),
    )
    destinationElements.splice(destinationIndex, 0, moveElement)
    destinationElements.map((value, i) => {
      value.index = i
      return value
    })

    // set new uischema
    let keyList = Object.keys(jsonForm.schema.mapKey)

    let destinationList = keyList.filter((key) => {
      let mapKey = jsonForm.schema.mapKey[key] || ''
      if (mapKey.split('.').includes(draggableId)) {
        return key
      }
      return null
    })

    // reset subGroup
    let layout = destinationMapKey.split('.').shift()
    let length = destinationMapKey.split('.').length
    destinationList.forEach((key) => {
      let startSubGroup = !headGroup
      let mapKey = jsonForm.schema.mapKey[key]
      let keyIndice = findUischemaIndex(key, mapKey)
      keyIndice.reduce(
        (o, i, index) => {
          if (
            o.elements[i] &&
            o.elements[i].uiType === 'container' &&
            index >= length
          ) {
            o.elements[i].subGroup = startSubGroup
            startSubGroup = !startSubGroup
          }
          o.elements[i].layout = layout
          return o.elements[i]
        },
        { elements },
      )
    })
    newUi = { ...newUi, elements: cloneDeep(elements) }
    setJsonForm({ ...jsonForm, uischema: newUi })
    setNewUi({
      move: true,
      source: sourceKey,
      key: moveElement.key,
      destination: destinationKey,
    })
  }

  const moveSchema = ({ source, destination, draggableId }) => {
    // prepare source schema
    let sourceIndex = source.index
    if (sourceIndex < 0) return
    let sourceKey = source.droppableId

    let sourceEnsuredData = ensureMapNameByUiKey({ key: sourceKey })
    let sourceMapName = sourceEnsuredData.mapName

    let schemaProps = jsonForm.schema.properties
    let sourceNameList = sourceMapName.split('.').filter(Boolean)
    let sourceSchema = findSchema(schemaProps, sourceNameList)

    // prepare move schema

    let dragEnsuredData = ensureMapNameByUiKey({ key: draggableId })
    let dragKey = dragEnsuredData.mapName

    let dragKeyList = dragKey.split('.').filter(Boolean)
    let dragName = dragKeyList.pop()
    if (!dragName) return
    let moveSchema = {}

    if (objectPath.has(sourceSchema || {}, ['properties', dragName])) {
      moveSchema = objectPath.get(sourceSchema, ['properties', dragName])
      // remove move schema from source
      objectPath.del(sourceSchema, ['properties', dragName])
    } else if (objectPath.has(schemaProps, dragName)) {
      // remove move schema from source
      moveSchema = schemaProps[dragName]
      delete schemaProps[dragName]
    }

    // prepare destination schema
    let destinationKey = destination.droppableId

    let destEnsuredData = ensureMapNameByUiKey({ key: destinationKey })
    let destinationMapName = destEnsuredData.mapName

    let destinationNameList = destinationMapName.split('.').filter(Boolean)
    let destinationSchema = findSchema(schemaProps, destinationNameList)

    if (!destinationSchema && destinationNameList.length > 0) {
      destinationSchema = destinationNameList.reduce(
        (schema, name) => {
          let nextSchema = objectPath.get(schema, ['properties', name])

          if (!nextSchema) {
            nextSchema = { type: 'object', properties: {} }
            objectPath.set(schema, ['properties', name], nextSchema)
          }

          nextSchema.properties = nextSchema.properties || {}
          return nextSchema
        },
        { properties: schemaProps },
      )
    }

    let newSchema = jsonForm.schema

    // check schema exist inside container
    if (destinationSchema) {
      // add move schema to destination (inside container)
      destinationSchema.properties = destinationSchema.properties || {}
      destinationSchema.properties[dragName] = moveSchema
      updateMapData(sourceKey, destinationKey, draggableId)
    } else {
      // add move schema to destination (outside container)
      schemaProps[dragName] = moveSchema
      updateMapData(sourceKey, destinationKey, draggableId)
    }

    // set new schema
    newSchema.properties = schemaProps
  }

  const updateMapData = (sourceKey, destinationKey, draggableId) => {
    let newSchema = jsonForm.schema
    let keyList = Object.keys(jsonForm.schema.mapKey)
    let destinationEnsuredData = ensureMapByUiKey({ key: destinationKey })
    let destinationMapKey = destinationEnsuredData.mapKey || destinationKey
    let destinationMapName = destinationEnsuredData.mapName || destinationKey
    let draggableMapName = jsonForm.schema.mapName[draggableId] || draggableId
    let draggableNameList = draggableMapName.split('.').filter(Boolean)
    let draggableName =
      draggableNameList[draggableNameList.length - 1] || draggableId

    const getMoveMapSuffix = (value, target) => {
      let list = (value || target).split('.').filter(Boolean)
      let index = list.indexOf(target)

      return index > -1 ? list.slice(index).join('.') : target
    }

    let sourceList = keyList.filter((key) => {
      let mapKey = jsonForm.schema.mapKey[key] || ''
      if (mapKey.split('.').includes(draggableId)) {
        return key
      }
      return null
    })

    let newMapKey = jsonForm.schema.mapKey
    let newMapName = jsonForm.schema.mapName

    sourceList.forEach((key) => {
      let newKey = newMapKey[key]
      let newName = newMapName[key]
      let keySuffix = getMoveMapSuffix(newKey, draggableId)
      let nameSuffix = getMoveMapSuffix(newName, draggableName)
      newMapKey[key] = [destinationMapKey, keySuffix].filter(Boolean).join('.')
      newMapName[key] = [destinationMapName, nameSuffix]
        .filter(Boolean)
        .join('.')
    })
    newSchema.mapKey = newMapKey
    newSchema.mapName = newMapName
  }

  const structuralDropAllow = {
    panelGroup: ['panel'],
    tab: ['tabPanel'],
    dynamic: ['dynamicPanel', 'editPanel'],
    modal: ['modalBody', 'modalFooter'],
  }

  const canDropDirectlyInDesigner = (draggableId, destinationKey) => {
    let destinationTag =
      objectPath.get(jsonForm, `schema.mapTag.${destinationKey}`) ||
      destinationKey ||
      ''
    let allowList = structuralDropAllow[destinationTag]

    if (!allowList) return true

    let draggableTag =
      objectPath.get(jsonForm, `schema.mapTag.${draggableId}`) ||
      draggableId ||
      ''

    return allowList.includes(draggableTag)
  }

  const getDesignerDropParentKey = (key) => {
    let targetUi =
      findUiByKey(jsonForm?.uischema?.elements || [], key) || {
        key,
      }
    let ensuredData = ensureMapKeyByUiKey(targetUi)
    let mapKey =
      ensuredData.mapKey ||
      objectPath.get(jsonForm, `schema.mapKey.${key}`) ||
      ''
    let keyList = mapKey.split('.').filter(Boolean)

    return keyList.length > 1 ? keyList[keyList.length - 2] : ''
  }

  const getDesignerDropElements = (dropKey) => {
    let targetDropUi =
      findUiByKey(jsonForm?.uischema?.elements || [], dropKey) || {
        key: dropKey,
      }
    let ensuredData = ensureMapKeyByUiKey(targetDropUi)
    let mapKey =
      ensuredData.mapKey ||
      objectPath.get(jsonForm, `schema.mapKey.${dropKey}`) ||
      dropKey
    let indices = findUischemaIndex(dropKey, mapKey, jsonForm.uischema.elements)

    if (!indices.length) return []

    let dropUi = findUiSchema(jsonForm.uischema.elements, indices)

    return objectPath.get(dropUi, 'elements') || []
  }

  const getDesignerDestinationIndex = ({
    draggableId,
    sourceKey,
    destinationKey,
    destinationIndex,
    destinationBeforeKey,
    destinationAfterKey,
  }) => {
    let destinationElements = getDesignerDropElements(destinationKey)
    let nextDestinationElements =
      sourceKey === destinationKey
        ? destinationElements.filter(({ key }) => key !== draggableId)
        : destinationElements

    if (destinationBeforeKey) {
      let beforeIndex = nextDestinationElements.findIndex(
        ({ key }) => key === destinationBeforeKey,
      )

      if (beforeIndex > -1) return beforeIndex
    }

    if (destinationAfterKey) {
      let afterIndex = nextDestinationElements.findIndex(
        ({ key }) => key === destinationAfterKey,
      )

      if (afterIndex > -1) return afterIndex + 1
    }

    return Math.max(
      0,
      Math.min(destinationIndex, nextDestinationElements.length),
    )
  }

  const moveDesignerUi = ({
    draggableId,
    destinationKey,
    destinationIndex,
    destinationBeforeKey,
    destinationAfterKey,
  }) => {
    let sourceKey = getDesignerDropParentKey(draggableId)

    if (!sourceKey || !destinationKey) return
    if (!canDropDirectlyInDesigner(draggableId, destinationKey)) return

    let sourceElements = getDesignerDropElements(sourceKey)
    let sourceIndex = sourceElements.findIndex(({ key }) => key === draggableId)

    if (sourceIndex < 0) return

    let safeDestinationIndex = getDesignerDestinationIndex({
      draggableId,
      sourceKey,
      destinationKey,
      destinationIndex,
      destinationBeforeKey,
      destinationAfterKey,
    })
    let result = {
      draggableId,
      source: {
        droppableId: sourceKey,
        index: sourceIndex,
      },
      destination: {
        droppableId: destinationKey,
        index: safeDestinationIndex,
      },
    }

    if (sourceKey === destinationKey) {
      reorderUiSchema(result)
      return
    }

    moveSchema(result)
    moveUiSchema(result)
  }

  const createDesignerUi = (result) => {
    if (!result?.destination) return

    let destinationKey = result.destination.droppableId

    if (!canDropDirectlyInDesigner(result.draggableId, destinationKey)) return

    let safeDestinationIndex = getDesignerDestinationIndex({
      draggableId: result.draggableId,
      sourceKey: result.source?.droppableId,
      destinationKey,
      destinationIndex: result.destination.index,
      destinationBeforeKey: result.destination.beforeKey,
      destinationAfterKey: result.destination.afterKey,
    })
    let nextResult = {
      ...result,
      destination: {
        droppableId: destinationKey,
        index: safeDestinationIndex,
      },
    }

    return createDesignAction(nextResult, {
      ...state,
      jsonForm,
      current,
      generateString,
      addUiForContainer,
      addUiForLayout,
      addInsideModal,
      addInsideTab,
      addInsideDynamic,
      reorderUiSchema,
      moveUiSchema,
      moveSchema,
      getDataUi,
      updateUiSchema,
      setDesignerPropsData,
    })
  }

  const updatePropData = (value, propKey) => {
    let designerPropsData = objectPath.get(state, 'jsonForm.designerPropsData')
    let mapProps = designerPropsData?.map || {}
    let filter = Object.keys(mapProps).filter(
      (key) => mapProps[key] === propKey,
    )
    if (filter.length > 0) {
      filter.forEach((key) => {
        let [component, propName] = key.replace('/', '.').split('.')
        objectPath.set(
          designerPropsData,
          `data.${component}.${propName}`,
          value,
        )
      })
    }
    setDesignerPropsData(designerPropsData)
  }

  const setComponentProps = (props) => {
    if (props) {
      let componentPropsData =
        objectPath.get(state, 'jsonForm.componentPropsData') || {}
      objectPath.set(componentPropsData, 'data', props)
      setComponentPropsData(componentPropsData)
    }
  }

  const formData = useMemo(() => {
    if (openModal && modalForm) {
      return modalForm?.formData || {}
    }
    return jsonForm?.formData || {}
  }, [openModal, jsonForm?.formData])

  const updateFormData = (ui, value) => {
    let newFormData = formData
    objectPath.set(newFormData, ui.key, value)
    let formCopy = updateCopy(ui.key, ui.copyData, value, newFormData)
    setFormData(formCopy)
    updatePropData(value, ui.key)
  }

  const updateCopy = (sourceKey, copyData, value, formData) => {
    if (!copyData) return formData
    let copyList = Object.keys(copyData)
    copyList.forEach((key) => {
      let checked = copyData[key]
      if (checked === sourceKey) {
        formData[key] = value
      }
    })
    return formData
  }

  const getCategories = async () => {
    let api = `/designer/form-widget-group`
    let payload = await httpClient.get(api).then(({ data }) => data.data)
    dispatch({ type: 'SET_CATEGORIES', payload })
    return payload
  }

  const getDashboardType = async () => {
    let api = `/designer/dashboard-type`
    let payload = await httpClient.get(api).then(({ data }) => data.data)
    dispatch({ type: 'SET_DASHBOARD_TYPE', payload })
    return payload
  }

  const getEmployee = async () => {
    let employeeList = state?.employeeList || []
    if (employeeList.length === 0) {
      let api = '/hr/employee/search?page=1&perPage=1000'
      let payload = await httpClient
        .get(api)
        .then(({ data }) => data?.data)
        .catch(() => [])
      dispatch({ type: 'SET_EMPLOYEE_LIST', payload })
    }
  }

  return (
    <Provider
      value={{
        ...state,
        setCurrent,
        setEditorKey,
        setEditorDropId,
        setGroupKey,
        setSubGroup,
        setSchema,
        setUischema,
        setFormData,
        setFieldFormData,
        setJsonForm,
        setRequireField,
        setValidation,
        setFormGroup,
        setBulkGroup,
        setApiGroup,
        setCustomGroup,
        setTableGroup,
        setCollection,
        setLayoutHistory,
        setFormRevisionId,
        setFormDesign,
        addSchema,
        addUiSchema,
        editUi,
        saveUi,
        findSchema,
        findUiSchema,
        findUischemaIndex,
        getCurrentUi,
        getCurrent,
        deleteUi,
        closeUi,
        deleteMapData,
        deleteCollection,
        updateUiSchema,
        setOpenModal,
        generateString,
        createMapForWidget,
        setNewUi,
        setTempUi,
        setTempModal,
        setTempComponent,
        resetContext,
        getModules,
        setModalForm,
        setSearchData,
        setFieldSearchData,
        setSearchResult,
        addUiForContainer,
        addUiForLayout,
        addInsideModal,
        addInsideTab,
        addInsideDynamic,
        reorderUiSchema,
        moveUiSchema,
        moveSchema,
        moveDesignerUi,
        createDesignerUi,
        createSchema,
        getDataUi,
        deleteSearch,
        setPageFormProps,
        setMapFormId,
        setMapPageId,
        setDataGET,
        setSendProps,
        setDesignerPropsData,
        updatePropData,
        setComponentProps,
        setComponentPropsData,
        setMapBind,
        setShowBindData,
        setShowBindParam,
        updateFormData,
        setIsAutoSave,
        setAutoSaveTime,
        setUpdatedAt,
        getCategories,
        setWidgetComponent,
        setModalComponent,
        setIsSyncWidget,
        getDashboardType,
        setOpenSetting,
        onFullscreen,
        setDashboardList,
        setFilterDashboard,
        setIsLoadingDashboard,
        setIsUiChanged,
        setIsSelectRow,
        setTableRefresh,
        setIsResetData,
        setTabParamActive,
        setTabActiveState,
        setFavorites,
        setDatasources,
        setIsAddDynamic,
        setIsLoading,
        setKeywordFilter,
        setAllData,
        setFieldAllData,
        setIsUpdateDynamic,
        setConditions,
        setShowInvisible,
        setElementAction,
        setPagination,
        setDisplayMode,
        setDatasourceValue,
        setIsInitialOption,
        setIsCheckedCondition,
        setDataset,
        setCurrentSearch,
        setActiveButton,
        setIsInputSearch,
        setWidgetJsonForm,
        setOpenModalCondition,
        setConditionProps,
        setCallback,
        registerDesignerOnChangeScope,
        setApiConditionGroup,
        setApiSearchTable,
        setDesignerEvent,
        setIsLoadingApi,
        setSelectApiOption,
        setAllFormGroup,
        getEmployee,
        setWidgetDesign,
        setDocumentJson,
        clearData,
        setIsClearData,
        setFormulas,
        setRules,
      }}
    >
      <div
        onChangeCapture={markDesignerOnChangeUserEvent}
        onClickCapture={markDesignerOnChangeUserEvent}
        onInputCapture={markDesignerOnChangeUserEvent}
        onKeyDownCapture={markDesignerOnChangeUserEvent}
        onMouseDownCapture={markDesignerOnChangeUserEvent}
        onPointerDownCapture={markDesignerOnChangeUserEvent}
        style={{ display: 'contents' }}
      >
        {children}
      </div>
    </Provider>
  )
}

export { DesignContext, DesignProvider }
